import { Menu, Bell, User, Moon, Sun, LogOut, UserCircle, Globe, Check } from "lucide-react";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useTheme } from "../utils/ThemeProvider";
import { useAuth } from "../contexts/AuthContext";
import { toTitleCase } from "../lib/utils";
import { useLanguage } from "../contexts/LanguageContext";
import { useNotifications } from "../contexts/NotificationContext";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

export function Header({ onMenuClick }) {
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const { language, changeLanguage, languages, currentLanguage } = useLanguage();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const { t } = useTranslation();

  const getUserName = () => {
    return toTitleCase(user?.full_name || user?.name || "User");
  };

  const getUserDepartment = () => {
    if (user?.department && typeof user.department === 'object') {
      return user.department.name || user.department.deptName || "Staff";
    }
    if (user?.department && typeof user.department === 'string') {
      return user.department;
    }
    return user?.department_name || "Staff";
  };

  const getUserImage = () => {
    const imageUrl = user?.picture_path || user?.picturePath || user?.image || user?.photo || user?.avatar;
    if (!imageUrl || imageUrl === 'null' || imageUrl === 'undefined') {
      return undefined;
    }
    return String(imageUrl).trim() || undefined;
  };

  const getUserInitials = () => {
    const name = user?.full_name || user?.name;
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return "U";
  };

  return (
    <header className="bg-card border-b border-border sticky top-0 z-50" style={{ overflow: 'visible', position: 'relative' }}>
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8" style={{ position: 'relative' }}>
        <div className="flex items-center justify-between h-16 relative">
          {/* Logo and Brand */}
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={onMenuClick}
            >
              <Menu className="size-5" />
            </Button>
            <div className="flex-shrink-0">
              <h1 className="text-primary">{t('common.staffPortal')}</h1>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 md:gap-4">
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === "light" ? <Moon className="size-5" /> : <Sun className="size-5" />}
            </Button>
            <div className="relative">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    type="button"
                    className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50 size-9 rounded-md"
                  >
                    <Globe className="size-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 z-[99999]" sideOffset={8}>
                  <DropdownMenuLabel>{t('common.selectLanguage')}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {languages.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        changeLanguage(lang.code);
                      }}
                      className="cursor-pointer"
                    >
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{lang.flag}</span>
                          <span>{lang.name}</span>
                        </div>
                        {language === lang.code && (
                          <Check className="size-4 text-[#1E74FD]" />
                        )}
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="relative">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    type="button"
                    className="relative inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50 size-9 rounded-md"
                  >
                    <Bell className="size-5" />
                    {unreadCount > 0 && (
                      <span className="absolute top-1 right-1 size-2 bg-red-500 rounded-full"></span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 z-[99999]" sideOffset={8}>
                  <DropdownMenuLabel className="flex items-center justify-between">
                    <span>{t('common.notifications')}</span>
                    {unreadCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          markAllAsRead();
                        }}
                        className="h-auto py-1 px-2 text-xs"
                      >
                        {t('common.markAllAsRead')}
                      </Button>
                    )}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {(!notifications || notifications.length === 0) ? (
                    <div className="px-2 py-8 text-center text-sm text-muted-foreground">
                      {t('common.noNotifications')}
                    </div>
                  ) : (
                    <div className="max-h-[400px] overflow-y-auto">
                      {notifications.slice(0, 10).map((notification) => (
                        <DropdownMenuItem
                          key={notification.id}
                          onClick={() => markAsRead(notification.id)}
                          className="flex flex-col items-start gap-1 p-3 cursor-pointer"
                        >
                          <div className="flex items-start justify-between w-full">
                            <p className="text-sm font-medium">{notification.title || 'Notification'}</p>
                            {!notification.read && (
                              <span className="size-2 bg-[#1E74FD] rounded-full flex-shrink-0 mt-1"></span>
                            )}
                          </div>
                          {notification.message && (
                            <p className="text-xs text-muted-foreground">{notification.message}</p>
                          )}
                          {notification.time && (
                            <p className="text-xs text-muted-foreground">{notification.time}</p>
                          )}
                        </DropdownMenuItem>
                      ))}
                    </div>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="relative">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    type="button"
                    className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50 size-9 rounded-md"
                  >
                    <Avatar className="size-8">
                      {getUserImage() && (
                        <AvatarImage 
                          src={getUserImage()} 
                          alt={getUserName()} 
                        />
                      )}
                      <AvatarFallback className="bg-gradient-to-br from-[#1E74FD] to-blue-600 text-white text-xs font-semibold">
                        {getUserInitials()}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="end" 
                  className="w-64 z-[99999]" 
                  sideOffset={8}
                >
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <div className="flex items-center gap-3">
                      <Avatar className="size-10">
                        {getUserImage() && (
                          <AvatarImage 
                            src={getUserImage()} 
                            alt={getUserName()} 
                          />
                        )}
                        <AvatarFallback className="bg-gradient-to-br from-[#1E74FD] to-blue-600 text-white text-sm font-semibold">
                          {getUserInitials()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col min-w-0">
                        <p className="text-sm font-semibold truncate">{getUserName()}</p>
                        <p className="text-xs text-muted-foreground truncate">{getUserDepartment()}</p>
                      </div>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="flex items-center w-full">
                    <UserCircle className="mr-2 size-4" />
                    <span>{t('common.profile')}</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={logout}
                  className="text-destructive focus:text-destructive cursor-pointer"
                >
                  <LogOut className="mr-2 size-4" />
                  <span>{t('common.logout')}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
